import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

function toPublicUrl(baseUrl, storedUrl) {
  const p = String(storedUrl || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(baseUrl || '').replace(/\/$/, '');
  const rel = p.startsWith('/') ? p : `/${p}`;
  return base + rel;
}

export default function APNClipsArchive() {
  const { user } = useContext(UserContext);
  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);

  const [filters, setFilters] = useState({ year: '', month: '', category: '', platform: '', creator: '' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const openItem = useMemo(() => items.find((x) => x.id === openId) || null, [items, openId]);
  const openVideoUrl = useMemo(() => (openItem ? toPublicUrl(baseUrl, openItem.storedUrl) : ''), [openItem, baseUrl]);

  const load = async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (String(nextFilters.year || '').trim()) params.year = String(nextFilters.year).trim();
      if (String(nextFilters.month || '').trim()) params.month = String(nextFilters.month).trim();
      if (String(nextFilters.category || '').trim()) params.category = String(nextFilters.category).trim();
      if (String(nextFilters.platform || '').trim()) params.platform = String(nextFilters.platform).trim();
      if (String(nextFilters.creator || '').trim()) params.creator = String(nextFilters.creator).trim();

      const res = await api.get('/apn-clips/archive/submissions', { params });
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || 'Konnte Archiv nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = async () => {
    const empty = { year: '', month: '', category: '', platform: '', creator: '' };
    setFilters(empty);
    await load(empty);
  };

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Archiv</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Durchsuche ältere Clips & Highlights.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips" style={{ textDecoration: 'none' }}>Zurück</Link>
          <button className="hero-btn-primary" type="button" onClick={() => load()} disabled={loading}>Suchen</button>
          <button className="hero-btn-secondary" type="button" onClick={reset} disabled={loading}>Reset</button>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Filter</h2>

          {error && (
            <div style={{ padding: '0 8px 12px', color: '#ffdddd', fontSize: 13 }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, padding: '0 8px 12px' }}>
            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Jahr
              <input
                value={filters.year}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((p) => ({ ...p, year: v, month: String(v || '').trim() ? p.month : '' }));
                }}
                placeholder="z.B. 2026"
                inputMode="numeric"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Monat
              <select
                value={filters.month}
                onChange={(e) => setFilters((p) => ({ ...p, month: e.target.value }))}
                disabled={!String(filters.year || '').trim()}
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(20,20,20,0.8)', color: '#fff' }}
              >
                <option value="">(alle)</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={String(m)}>{String(m).padStart(2, '0')}</option>
                ))}
              </select>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Monat filtert nur zusammen mit Jahr.</div>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Kategorie
              <input
                value={filters.category}
                onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                placeholder="z.B. Shooter"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Plattform
              <input
                value={filters.platform}
                onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
                placeholder="z.B. Twitch"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: 13, opacity: 0.9 }}>
              Creator
              <input
                value={filters.creator}
                onChange={(e) => setFilters((p) => ({ ...p, creator: e.target.value }))}
                placeholder="Name"
                style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
              />
            </label>
          </div>

          <div style={{ padding: '0 8px 8px' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <button className="hero-btn-primary" type="button" onClick={() => load()} disabled={loading}>Suchen</button>
              <button className="hero-btn-secondary" type="button" onClick={reset} disabled={loading}>Reset</button>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Es werden nur freigegebene Clips angezeigt – ohne den aktuell laufenden Monat.
              </div>
            </div>

            {loading ? (
              <div style={{ opacity: 0.8 }}>Lade…</div>
            ) : items.length === 0 ? (
              <div style={{ opacity: 0.85 }}>Keine Treffer.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                {items.map((s) => {
                  const id = s?.id;
                  const thumb = String(s?.thumbnailUrl || '').trim();
                  const title = String(s?.title || '(ohne Titel)');
                  const creator = String(s?.creatorName || '—');
                  const platform = String(s?.platform || '—');
                  const category = String(s?.category || '—');

                  return (
                    <div key={id} className="highlight-card" style={{ ...card, alignItems: 'stretch', display: 'grid', gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => setOpenId(id)}
                        style={{
                          padding: 0,
                          border: '1px solid rgba(255,255,255,0.10)',
                          borderRadius: 12,
                          overflow: 'hidden',
                          background: 'transparent',
                          cursor: 'pointer',
                        }}
                        title="Clip ansehen"
                      >
                        {thumb ? (
                          <img
                            alt={title}
                            src={thumb}
                            style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div style={{ height: 150, display: 'grid', placeItems: 'center', opacity: 0.8 }}>
                            Kein Thumbnail
                          </div>
                        )}
                      </button>

                      <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>{title}</div>
                      <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.35 }}>
                        <div><b>Creator:</b> {creator}</div>
                        <div><b>Plattform:</b> {platform}</div>
                        <div><b>Kategorie:</b> {category}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {openItem && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="highlight-card"
            style={{
              ...card,
              width: '100%',
              maxWidth: 980,
              alignItems: 'stretch',
              display: 'grid',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                {String(openItem?.title || '(ohne Titel)')}
              </div>
              <button type="button" className="hero-btn-secondary" onClick={() => setOpenId(null)}>Schließen</button>
            </div>

            {openVideoUrl ? (
              <video
                controls
                src={openVideoUrl}
                style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
              />
            ) : (
              <div style={{ opacity: 0.85, fontSize: 13 }}>Video ist nicht verfügbar.</div>
            )}

            <div style={{ fontSize: 13, opacity: 0.9, display: 'grid', gap: 4 }}>
              <div><b>Creator:</b> {String(openItem?.creatorName || '—')}</div>
              <div><b>Plattform:</b> {String(openItem?.platform || '—')}</div>
              <div><b>Kategorie:</b> {String(openItem?.category || '—')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
